import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Stack,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  Button,
  Skeleton,
  CircularProgress
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Box } from '@mui/system';
import { formatDateRange, isArrayBlank, parseIsoDate } from '../../utils/Utils';
import { fetchActualTemplateData } from './service';
import { useOkta } from '../../hooks/oktaHook';
import { useDispatch, useSelector } from 'react-redux';
import CalendarModal from '../../components/CalendarCards/CalendarModal';
import { setTemplateDataRx } from '../../redux/Calendar/calendarSlice';
import { startOfToday } from 'date-fns';

const ProjectTimelineCard = ({
  title = 'Project Timeline',
  sub = 'Select Start Date',
  isRequired = true,
  date,
  setPrincipalStartDate
}) => {
  const handleDateChange = (newDate) => {
    if (!newDate) return;
    setPrincipalStartDate(new Date(newDate));
  };

  return (
    <Stack
      bgcolor="#fbfcfe"
      borderRadius="8px"
      boxShadow="0px 0px 24px rgba(0, 92, 188, 0.08)"
      sx={{ p: 2 }}>
      <Stack direction="column" spacing={3}>
        <Typography variant="h6" fontWeight={700} color="#3a3a3a">
          {title}
        </Typography>

        <Stack direction="column" spacing={1} sx={{ width: '240px' }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="#3a3a3a">
              {sub}
            </Typography>
            {isRequired && (
              <Typography variant="caption" color="black">
                *Required
              </Typography>
            )}
          </Stack>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={date ? new Date(date) : null}
              onChange={handleDateChange}
              minDate={startOfToday()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select a date"
                  sx={{
                    bgcolor: '#fbfcfe',
                    borderRadius: '4px',
                    border: '1px solid #c0c0c0',
                    '.MuiOutlinedInput-notchedOutline': { border: 0 },
                    '.MuiOutlinedInput-input': {
                      padding: '8px',
                      color: date ? '#3a3a3a' : '#a0a0a0'
                    }
                  }}
                />
              )}
              inputFormat="MM/dd/yyyy"
              format="MM/dd/yyyy"
            />
          </LocalizationProvider>
        </Stack>
      </Stack>
    </Stack>
  );
};

const PrincipleTasks = ({ templateData, enableEdit }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  const handleOpenModal = (taskId) => {
    setSelectedTaskId(taskId);
    setIsModalOpen(true);
  };

  // Function to close the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <Stack
      bgcolor="#fbfcfe"
      borderRadius="8px"
      boxShadow="0px 0px 24px rgba(0, 92, 188, 0.08)"
      sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom fontWeight={700} color="#3a3a3a">
        End To End Principle Tasks
      </Typography>
      <Grid container spacing={2}>
        {templateData?.map((task, index) => {
          const taskTimeDuration = task?.timeDuration;
          const period = formatDateRange(taskTimeDuration);

          return (
            <Grid item xs={12} sm={6} md={3} key={task.taskId}>
              <Card
                sx={{
                  borderRadius: 2,
                  borderColor: '#d7e5f8',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  boxShadow: '0px 0px 24px rgba(0, 92, 188, 0.08)'
                }}>
                <CardContent>
                  <Typography variant="dense" fontWeight="bold" sx={{ color: '#3a3a3a' }}>
                    {task.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#3a3a3a' }}>
                    {period}
                    <br />
                    {task?.periodWeeks} Week{task?.periodWeeks > 1 ? 's' : ''}
                    <br />
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      margin: '0px',
                      padding: '0px'
                    }}>
                    <Button
                      variant="outlined"
                      sx={{ borderColor: '#005cbcff', color: '#005cbcff' }}
                      disabled={!enableEdit}
                      onClick={() => handleOpenModal(task.taskId)}>
                      Edit
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        <CalendarModal
          open={isModalOpen}
          handleClose={handleCloseModal}
          selectedTaskId={selectedTaskId}
          screenId="template"
        />
      </Grid>
    </Stack>
  );
};

function Template({ title, onCancel, onSubmit, submitLoad, enableEdit }) {
  const {
    userDetails: { email, roles }
  } = useOkta();
  const { search } = useLocation();

  const newParams = new URLSearchParams(search);
  const selectedCycleId = Number(newParams.get('cycleId'));
  const selectedBu = newParams.get('bu');

  const [date, setDate] = useState(); // need to select a date from datepicker first
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const templateDataRx = useSelector((state) => state.calendar.updatedData);

  /* The `getTemplateData` function is a custom callback function created using the `useCallback` hook
  in React. It is an asynchronous function that is responsible for fetching template data based on
  the selected date. */

  const getTemplateData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchActualTemplateData({
        startDate: parseIsoDate(date),
        userEmail: email,
        buName: selectedBu,
        role: roles,
        tasksList: [],
        cycleId: selectedCycleId
      });
      setIsLoading(false);
      dispatch(setTemplateDataRx(response));
    } catch (error) {
      console.error('Error fetching template data:', error);
      setIsLoading(false);
    }
  }, [date]);

  // need to make api call on date change for template card data,
  useEffect(() => {
    if (date && selectedCycleId) {
      getTemplateData();
    }
  }, [date, getTemplateData]);

  const isTemplateDataBlank = isArrayBlank(templateDataRx); // changed from local state to redux templateData

  return (
    <Stack sx={{ gap: 2, width: '100%', p: 3 }}>
      {/* date selection stack */}
      <ProjectTimelineCard
        title={title}
        date={date}
        setPrincipalStartDate={(val) => setDate(val)}
      />
      {/* if no date and isloading , render skeleton */}
      {date && isLoading ? (
        <Stack spacing={2}>
          {[...Array(4)].map((_, index) => (
            <Skeleton key={`${index + 1}`} variant="rectangular" height={60} />
          ))}
        </Stack>
      ) : // when it's not loading and both date and template data is present render PrincipalTasks
      date && !isTemplateDataBlank && !isLoading ? (
        <PrincipleTasks templateData={templateDataRx} enableEdit={enableEdit} />
      ) : null}
      <Stack direction={'row'} justifyContent={'flex-end'} gap={2}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={isLoading || !date}
          type="submit"
          endIcon={submitLoad && <CircularProgress size={20} color="inherit" />}>
          Save
        </Button>
      </Stack>
    </Stack>
  );
}

export default Template;
